import {WebTracerProvider} from '@opentelemetry/sdk-trace-web';
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http';
import {BatchSpanProcessor} from '@opentelemetry/sdk-trace-base';
import {getWebAutoInstrumentations} from '@opentelemetry/auto-instrumentations-web';
import {registerInstrumentations} from '@opentelemetry/instrumentation';
import {resourceFromAttributes} from '@opentelemetry/resources';
import {ATTR_SERVICE_NAME, ATTR_DEPLOYMENT_ENVIRONMENT_NAME} from '@opentelemetry/semantic-conventions';
import {CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator} from '@opentelemetry/core';
import {ZoneContextManager} from '@opentelemetry/context-zone';
import {Context, Span, trace} from '@opentelemetry/api';
import {getCurrentNavigationSpan} from './otel-state';
import {environment} from '@environments/environment';
import {collectorUrlPattern, stripQueryString} from './telemetry.util';

/**
 * Parents otherwise-unparented spans (e.g. HTTP requests fired during a route change) to the
 * current "Page" navigation span so routing-related requests show up under the navigation in
 * the trace waterfall.
 *
 * Known limitation: this injects the navigation span whenever no span is already active in the
 * Zone context. Any concurrent background work that runs inside the Angular Zone during an
 * active navigation — auto-refresh timers (e.g. AgAutoRefreshComponent), intervals, polling
 * requests — will also have its spans parented to the navigation span. This can inflate the
 * navigation span's apparent duration and pollute its waterfall. Spans created with an explicit
 * parent context are unaffected (the `!trace.getSpan(ctx)` guard skips them). If background work
 * needs to be isolated, run it outside the Angular Zone (NgZone.runOutsideAngular) so it does
 * not pick up the navigation context.
 */
export class NavigationAwareContextManager extends ZoneContextManager {
  override active(): Context {
    const ctx = super.active();
    const navSpan = getCurrentNavigationSpan();
    if (!trace.getSpan(ctx) && navSpan) {
      return trace.setSpan(ctx, navSpan);
    }
    return ctx;
  }
}

/**
 * Rewrites the span's URL attribute to its path-only form (query string dropped), so secrets passed
 * as query params (e.g. `/v2/api/thing?token=…`) are never exported to the collector. This matches
 * the sanitization already done for RUM (`datadog-rum.ts` beforeSend) and navigation spans
 * (`otel-navigation.service.ts`).
 *
 * The fetch/XHR instrumentations record the request URL under `http.url` because this project runs
 * on the default (OLD) HTTP semantic conventions — we don't set `OTEL_SEMCONV_STABILITY_OPT_IN`, so
 * `http.url` is the only URL attribute emitted. If that opt-in is ever switched to `http`/`http/dup`,
 * the URL moves to (or is duplicated in) `url.full`, and this must be updated to strip that key too.
 */
function stripSpanUrl(span: Span, url: string): void {
  span.setAttribute('http.url', stripQueryString(url));
}

/**
 * Builds the web auto-instrumentation config. Extracted (and exported) so the collector `ignoreUrls`
 * wiring and the query-string sanitizer can be unit-tested without standing up the whole SDK.
 */
export function buildWebInstrumentationConfig(
  apiUrlPattern: RegExp,
  collectorPattern: RegExp
): Parameters<typeof getWebAutoInstrumentations>[0] {
  return {
    '@opentelemetry/instrumentation-document-load': { enabled: false },
    '@opentelemetry/instrumentation-xml-http-request': {
      enabled: true,
      propagateTraceHeaderCorsUrls: [apiUrlPattern],
      ignoreUrls: [collectorPattern],
      applyCustomAttributesOnSpan: (span, xhr) => {
        if (xhr.responseURL) {
          stripSpanUrl(span, xhr.responseURL);
        }
      },
    },
    '@opentelemetry/instrumentation-fetch': {
      enabled: true,
      propagateTraceHeaderCorsUrls: [apiUrlPattern],
      ignoreUrls: [collectorPattern],
      applyCustomAttributesOnSpan: (span, request, result) => {
        const url =
          request instanceof Request
            ? request.url
            : result instanceof Response
              ? result.url
              : undefined;
        if (url) {
          stripSpanUrl(span, url);
        }
      },
    },
  };
}

/**
 * Flushes whatever the span processor still has buffered when the page goes away. `pagehide` covers
 * both unload and bfcache-into-cache transitions across browsers; without this the
 * BatchSpanProcessor drops every span buffered between its last batch and the page going away.
 *
 * Extracted (and exported) so this can be unit-tested without standing up the whole SDK — its
 * failure mode is silent span loss, which nothing downstream would surface. Typed structurally
 * rather than against `WebTracerProvider` so a test needs only a `forceFlush` stub.
 */
export function registerFlushOnPagehide(provider: {forceFlush(): Promise<void>}): void {
  window.addEventListener('pagehide', () => {
    provider.forceFlush().catch(() => undefined);
  });
}

let _provider: WebTracerProvider | null = null;

export function initOtel(): void {
  if (!environment.otelEnabled || _provider) {
    return;
  }

  const exporter = new OTLPTraceExporter({
    url: `${environment.otelCollectorUrl}/v1/traces`,
  });

  _provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: environment.datadog.service,
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment.env,
    }),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  _provider.register({
    contextManager: new NavigationAwareContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  });

  // Derive the trace-header propagation allow-list from the configured API base path so it is
  // centrally controlled rather than duplicated as a literal here.
  const apiUrlPattern = new RegExp(environment.apiBaseUrl);
  // Self-span noise: the OTLP exporter POSTs batches to the collector at
  // `${otelCollectorUrl}/v1/traces`. Without this exclusion the fetch/XHR instrumentation traces
  // each of those exports, producing HTTP-client spans about sending telemetry. Ignore the collector
  // endpoint (shared pattern with datadog-rum.ts) so the SDK never traces its own exports.
  const collectorPattern = collectorUrlPattern();

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations(buildWebInstrumentationConfig(apiUrlPattern, collectorPattern)),
    ],
  });

  registerFlushOnPagehide(_provider);
}
